import { PrismicNextImage } from '@prismicio/next';
import Image from 'next/image';
import { ImageFieldImage } from '@prismicio/client';
import { ShareOnSocialMedia } from '../common/ShareOnSocialMedia';

const DEVSU_LOGO_URL =
  'https://images.prismic.io/devsucom2024/ZyO63a8jQArT0F9p_DevsuLogo.png?auto=format%2Ccompress&fit=max&w=1080';

interface BlogMetaBarProps {
  authorName: string;
  /** Prismic author avatar; falls back to the Devsu logo when empty. */
  authorAvatar?: ImageFieldImage | null;
  /** ISO date string, rendered as e.g. "25 February, 2025". */
  date: string;
  readDuration?: number | null;
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Light-grey blog info bar shown under the hero: author + date + read time on the
 * left, "Share this article" + social buttons on the right. Kept as a single
 * molecular component (it's small and the author badge isn't reused elsewhere);
 * the share controls reuse the existing `ShareOnSocialMedia`.
 */
export function BlogMetaBar({
  authorName,
  authorAvatar,
  date,
  readDuration,
}: BlogMetaBarProps) {
  return (
    <div className="flex flex-col gap-4 rounded-[8px] bg-[#f4f4f4] px-[15px] py-4 md:min-h-[70px] md:flex-row md:items-center md:justify-between md:gap-6 md:py-3 lg:py-0">
      {/* Left: author + date + read time.
          - mobile / tablet (md): avatar in column 1; column 2 stacks the author
            name (row 1) and the date + read time (row 2).
          - desktop (lg): everything on a single inline row. */}
      <div className="flex items-center gap-3 lg:gap-6">
        {/* Column 1 — circular avatar */}
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg">
          {authorAvatar?.url ? (
            <PrismicNextImage
              field={authorAvatar}
              className="h-full w-full object-cover"
              fallbackAlt=""
            />
          ) : (
            <Image
              src={DEVSU_LOGO_URL}
              alt={authorName}
              width={36}
              height={36}
              className="h-full w-full object-cover"
            />
          )}
        </div>

        {/* Column 2 — name (row 1) + date/read (row 2); inline on desktop */}
        <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-6">
          <span className="font-inter text-[16px] font-semibold leading-[1.5] text-[#041118]">
            {authorName}
          </span>
          <div className="flex items-center gap-x-6 whitespace-nowrap">
            <span className="font-inter text-[16px] font-normal leading-[1.5] text-[#041118]">
              {formatDate(date)}
            </span>
            {!!readDuration && (
              <span className="font-inter text-[16px] font-normal leading-[1.5] text-[#041118]">
                {readDuration} minutes read
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right: share — centered on its own row on mobile, left-packed once it
          sits inline (md+, where the bar's justify-between places it right). */}
      <div className="flex items-center justify-center gap-4 md:justify-start">
        <span className="font-inter text-[16px] font-semibold leading-[1.5] text-black">
          Share this article
        </span>
        <ShareOnSocialMedia
          className="flex gap-2"
          bgColor="bg-[#f4f4f4]"
          fill="black"
        />
      </div>
    </div>
  );
}
